import React, { Suspense, lazy, useEffect, useState } from "react";
import { FlaskConical } from "lucide-react";
import LabAccessGate from "./LabAccessGate";
import LabUpgradeSplash from "./LabUpgradeSplash";
import LabDashboard from "./LabDashboard";
import LabRecommendations from "./LabRecommendations";
import LabHistory from "./LabHistory";
import LabSettings from "./LabSettings";
import LabExperimentsList from "./LabExperimentsList";
import LabExperimentDetail from "./LabExperimentDetail";
import LabProgressBar from "./LabProgressBar";
import { parseLabRoute } from "./labRouter";

// Submit page lazy-loads `yaml` and is the heaviest screen — keep off the
// critical path until the user hits "New experiment".
const LabSubmit = lazy(() => import("./LabSubmit"));

const Loader = () => (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-500">
            <FlaskConical className="w-5 h-5 animate-pulse" />
            <span>Loading…</span>
        </div>
    </div>
);

/**
 * Renders the right Lab page for the current URL. Treats /lab/upgrade as
 * the public splash (no access check); everything else goes through
 * LabAccessGate.
 */
const LabApp = () => {
    const [route, setRoute] = useState(parseLabRoute());

    useEffect(() => {
        const handler = () => setRoute(parseLabRoute());
        window.addEventListener("popstate", handler);
        return () => window.removeEventListener("popstate", handler);
    }, []);

    // Baseline: after every route change, schedule a nav-end ~300ms later.
    // Pages with heavier fetches can extend this by calling
    // useLabPageReady(loading) from LabProgressBar, which re-fires nav-end
    // when their loading boolean flips false.
    useEffect(() => {
        const t = setTimeout(() => {
            window.dispatchEvent(new Event("lab:nav-end"));
        }, 300);
        return () => clearTimeout(t);
    }, [route]);

    if (route.route === "upgrade") {
        return <LabUpgradeSplash />;
    }

    const renderInner = () => {
        switch (route.route) {
            case "dashboard":         return <LabDashboard />;
            case "submit":            return <Suspense fallback={<Loader />}><LabSubmit /></Suspense>;
            case "experiments":       return <LabExperimentsList />;
            case "experiment-detail": return <LabExperimentDetail runId={route.runId} />;
            case "recommendations":   return <LabRecommendations />;
            case "history":           return <LabHistory />;
            case "settings":          return <LabSettings />;
            default:
                return (
                    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center text-sm text-slate-500">
                        Page not found.
                    </div>
                );
        }
    };

    return (
        <>
            <LabProgressBar />
            <LabAccessGate>{renderInner()}</LabAccessGate>
        </>
    );
};

export default LabApp;
